'use client';
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { CheckCircle, AlertCircle, Info, X, UserPlus, MessageSquare, Users } from 'lucide-react';

export type NotificationType =
  | 'client_intake'
  | 'message_delivery'
  | 'staff_action'
  | 'success'
  | 'error'
  | 'info';
export type NotificationAudience = 'group' | 'individual';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  is_read: boolean;
  audience: NotificationAudience;
  staff_email?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface Toast {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  duration?: number;
}

interface NotificationContextValue {
  notifications: Notification[];
  groupNotifications: Notification[];
  individualNotifications: Notification[];
  unreadCount: number;
  unreadGroupCount: number;
  unreadIndividualCount: number;
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  addNotification: (n: Omit<Notification, 'id' | 'created_at' | 'is_read'>) => Promise<void>;
  addGroupNotification: (
    n: Omit<Notification, 'id' | 'created_at' | 'is_read' | 'audience' | 'staff_email'>
  ) => Promise<void>;
  addIndividualNotification: (
    n: Omit<Notification, 'id' | 'created_at' | 'is_read' | 'audience'> & { staff_email: string }
  ) => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}

// ─── Toast Component ────────────────────────────────────────────────────────
function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const duration = toast.duration ?? 5000;
    timerRef.current = setTimeout(() => onRemove(toast.id), duration);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [toast.id, toast.duration, onRemove]);

  const config: Record<
    NotificationType,
    { bg: string; border: string; icon: React.ReactNode; iconColor: string }
  > = {
    client_intake: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: <UserPlus size={18} />,
      iconColor: 'text-blue-600',
    },
    message_delivery: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: <MessageSquare size={18} />,
      iconColor: 'text-green-600',
    },
    staff_action: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      icon: <Users size={18} />,
      iconColor: 'text-purple-600',
    },
    success: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: <CheckCircle size={18} />,
      iconColor: 'text-green-600',
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: <AlertCircle size={18} />,
      iconColor: 'text-red-600',
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: <Info size={18} />,
      iconColor: 'text-blue-600',
    },
  };

  const c = config[toast.type] ?? config.info;

  return (
    <div
      className={`flex items-start gap-3 w-80 rounded-xl shadow-lg border p-4 ${c.bg} ${c.border} animate-slide-in-right`}
      role="alert"
    >
      <span className={`flex-shrink-0 mt-0.5 ${c.iconColor}`}>{c.icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 leading-tight">{toast.title}</p>
        <p className="text-xs text-gray-600 mt-0.5 leading-snug">{toast.message}</p>
      </div>
      <button
        onClick={() => onRemove(toast.id)}
        className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}

function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: string) => void }) {
  if (toasts.length === 0) return null;
  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem toast={t} onRemove={onRemove} />
        </div>
      ))}
    </div>
  );
}

// ─── Provider ────────────────────────────────────────────────────────────────
export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);

  const supabase = createClient();

  // Get current user email for individual notice filtering
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.email) {
        setCurrentUserEmail(data.user.email);
      }
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentUserEmail(session?.user?.email ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const refreshNotifications = useCallback(async () => {
    try {
      // Fetch group-wide notifications
      const groupQuery = supabase
        .from('notifications')
        .select('*')
        .eq('audience', 'group')
        .order('created_at', { ascending: false })
        .limit(50);

      let individualQuery = null;
      if (currentUserEmail) {
        individualQuery = supabase
          .from('notifications')
          .select('*')
          .eq('audience', 'individual')
          .eq('staff_email', currentUserEmail)
          .order('created_at', { ascending: false })
          .limit(50);
      }

      const [groupResult, individualResult] = await Promise.all([
        groupQuery,
        individualQuery ?? Promise.resolve({ data: [] }),
      ]);

      const groupData = (groupResult.data ?? []) as Notification[];
      const individualData = (individualResult.data ?? []) as Notification[];

      // Also fetch legacy notifications (no audience column set) — treat as group
      const legacyQuery = supabase
        .from('notifications')
        .select('*')
        .is('audience', null)
        .order('created_at', { ascending: false })
        .limit(50);
      const legacyResult = await legacyQuery;
      const legacyData = ((legacyResult.data ?? []) as Notification[]).map((n) => ({
        ...n,
        audience: 'group' as NotificationAudience,
      }));

      // Merge and deduplicate by id
      const allMap = new Map<string, Notification>();
      [...groupData, ...legacyData, ...individualData].forEach((n) => allMap.set(n.id, n));
      const merged = Array.from(allMap.values()).sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setNotifications(merged);
    } catch {
      // silently fail
    }
  }, [supabase, currentUserEmail]);

  useEffect(() => {
    refreshNotifications();

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications' },
        (payload) => {
          const newNotif = payload.new as Notification;
          // Only show if it's a group notice OR targeted at current user
          const isGroup = !newNotif.audience || newNotif.audience === 'group';
          const isForMe =
            newNotif.audience === 'individual' && newNotif.staff_email === currentUserEmail;
          if (isGroup || isForMe) {
            setNotifications((prev) => {
              if (prev.find((n) => n.id === newNotif.id)) return prev;
              return [{ ...newNotif, audience: newNotif.audience ?? 'group' }, ...prev];
            });
            addToast({
              type: newNotif.type as NotificationType,
              title: newNotif.title,
              message: newNotif.message,
              duration: 6000,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refreshNotifications, supabase, currentUserEmail]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addNotification = useCallback(
    async (n: Omit<Notification, 'id' | 'created_at' | 'is_read'>) => {
      try {
        const { data } = await supabase
          .from('notifications')
          .insert({
            type: n.type,
            title: n.title,
            message: n.message,
            audience: n.audience ?? 'group',
            staff_email: n.staff_email ?? null,
            metadata: n.metadata ?? {},
          })
          .select()
          .single();
        if (data) {
          setNotifications((prev) => [data as Notification, ...prev]);
          addToast({ type: n.type, title: n.title, message: n.message });
        }
      } catch {
        const localNotif: Notification = {
          id: `local-${Date.now()}`,
          type: n.type,
          title: n.title,
          message: n.message,
          is_read: false,
          audience: n.audience ?? 'group',
          staff_email: n.staff_email ?? null,
          metadata: n.metadata,
          created_at: new Date().toISOString(),
        };
        setNotifications((prev) => [localNotif, ...prev]);
        addToast({ type: n.type, title: n.title, message: n.message });
      }
    },
    [supabase, addToast]
  );

  const addGroupNotification = useCallback(
    async (n: Omit<Notification, 'id' | 'created_at' | 'is_read' | 'audience' | 'staff_email'>) => {
      return addNotification({ ...n, audience: 'group', staff_email: null });
    },
    [addNotification]
  );

  const addIndividualNotification = useCallback(
    async (
      n: Omit<Notification, 'id' | 'created_at' | 'is_read' | 'audience'> & { staff_email: string }
    ) => {
      return addNotification({ ...n, audience: 'individual' });
    },
    [addNotification]
  );

  const markAsRead = useCallback(
    async (id: string) => {
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      try {
        await supabase.from('notifications').update({ is_read: true }).eq('id', id);
      } catch {
        /* silent */
      }
    },
    [supabase]
  );

  const markAllAsRead = useCallback(async () => {
    const unreadIds = notifications.filter((n) => !n.is_read).map((n) => n.id);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    try {
      if (unreadIds.length > 0) {
        await supabase.from('notifications').update({ is_read: true }).in('id', unreadIds);
      }
    } catch {
      /* silent */
    }
  }, [supabase, notifications]);

  const groupNotifications = notifications.filter((n) => !n.audience || n.audience === 'group');
  const individualNotifications = notifications.filter((n) => n.audience === 'individual');
  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const unreadGroupCount = groupNotifications.filter((n) => !n.is_read).length;
  const unreadIndividualCount = individualNotifications.filter((n) => !n.is_read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        groupNotifications,
        individualNotifications,
        unreadCount,
        unreadGroupCount,
        unreadIndividualCount,
        toasts,
        addToast,
        removeToast,
        markAsRead,
        markAllAsRead,
        addNotification,
        addGroupNotification,
        addIndividualNotification,
        refreshNotifications,
      }}
    >
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </NotificationContext.Provider>
  );
}
